import { useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import SiteHeader from '../layout/SiteHeader';
import SiteFooter from '../layout/SiteFooter';
import HomePage from '../pages/home/HomePage';
import ModulePage from '../pages/modules/ModulePage';
import OpenSourcePage from '../pages/modules/OpenSourcePage';
import ContactPage from '../pages/modules/ContactPage';
import CompanyPage from '../pages/modules/CompanyPage';
import NotFoundPage from '../pages/modules/NotFoundPage';
import { getModule, modules } from '../content/modules';
import { brand } from '../content/site';
import {
  getMessages,
  hasLocalePrefix,
  localeFromPath,
  localizedPath,
  pathWithoutLocale,
} from '../i18n/locale';

export default function App() {
  const location = useLocation();
  const locale = localeFromPath(location.pathname);
  const messages = getMessages(locale);
  const pagePath = pathWithoutLocale(location.pathname);
  const previousLocation = useRef(null);
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('junoyi-theme') || 'light';
    } catch {
      return 'light';
    }
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem('junoyi-theme', theme);
    } catch {}
  }, [theme]);

  useEffect(() => {
    document.documentElement.lang = messages.meta.htmlLang;
    if (
      !previousLocation.current ||
      previousLocation.current.pathname !== location.pathname ||
      previousLocation.current.hash !== location.hash
    ) {
      previousLocation.current = { pathname: location.pathname, hash: location.hash };
      if (location.hash) {
        document
          .getElementById(decodeURIComponent(location.hash.slice(1)))
          ?.scrollIntoView?.({ behavior: 'smooth' });
      } else {
        window.scrollTo(0, 0);
      }
    }
    const moduleKey = Object.keys(modules).find((key) => modules[key].path === pagePath);
    const module = moduleKey ? getModule(moduleKey, locale) : null;
    const projectSlug = pagePath.startsWith('/open-source/') ? pagePath.split('/')[2] : null;
    const openSourceProject = messages.openSourcePage.projects.find(
      (project) => project.slug === projectSlug,
    );
    const companySection =
      pagePath === '/company/story'
        ? messages.companyPage.story.name
        : pagePath === '/company/community'
          ? messages.companyPage.community.name
          : null;
    document.title =
      pagePath === '/'
        ? `${brand.name} — Build. Share. Evolve.`
        : `${openSourceProject?.name || companySection || (pagePath.startsWith('/company') ? (pagePath === '/company/contact' ? messages.contactPage.name : messages.companyPage.name) : module?.name) || messages.notFound.name} · ${brand.name}`;
  }, [location.pathname, location.hash, locale, pagePath, messages]);

  if (!hasLocalePrefix(location.pathname)) {
    return (
      <Navigate
        to={`${localizedPath('zh', location.pathname)}${location.search}${location.hash}`}
        replace
      />
    );
  }

  return (
    <div className="site" id="top">
      <SiteHeader
        theme={theme}
        onThemeToggle={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      />
      <Routes>
        <Route path="/:locale" element={<HomePage theme={theme} />} />
        {/* Standalone product placeholder is intentionally hidden; send old links to the homepage preview. */}
        <Route
          path="/:locale/products"
          element={<Navigate to={`${localizedPath(locale)}#products`} replace />}
        />
        <Route path="/:locale/open-source" element={<OpenSourcePage />} />
        <Route
          path="/:locale/open-source/:projectSlug/docs"
          element={<OpenSourcePage view="docs" />}
        />
        <Route path="/:locale/open-source/:projectSlug" element={<OpenSourcePage />} />
        <Route path="/:locale/company" element={<CompanyPage />} />
        <Route path="/:locale/company/story" element={<CompanyPage section="story" />} />
        <Route path="/:locale/company/community" element={<CompanyPage section="community" />} />
        {Object.entries(modules)
          .filter(([key]) => !['products', 'open-source', 'company'].includes(key))
          .map(([key, module]) => (
            <Route
              key={key}
              path={`/:locale${module.path}`}
              element={<ModulePage module={getModule(key, locale)} />}
            />
          ))}
        <Route path="/:locale/company/contact" element={<ContactPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <SiteFooter />
    </div>
  );
}
